import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useFetch } from '@core/hooks'

import { Box } from '@components/atoms'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import {
  StyledUploadPicField,
  ImageWidgetStyled,
  FieldLabel,
  ErrorMessage,
  HelperMessage
} from './UploadPicField.styled'

import { noop } from 'lodash'
import axios from 'axios'
import * as mime from 'mime-types'
import { v4 as uuid } from 'uuid'
import { getFileExtension, callToast } from '@core/utils'
import regex from '@constants/regex'

import { UPLOAD_POLICIES } from '@constants/files'
import { APIS } from '@constants/apis'
import { buildCompanyStorageRequest } from '@core/utils/companyStorage'
import { useTranslation } from 'react-i18next'

const UploadPicField = ({
  name,
  label,
  error,
  helperMessage,
  value,
  className,
  optionalPayload,
  storageCompanyId,
  storageAssetType,
  uploadKey = 'key',
  setFieldValue = noop,
  required = false,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [image, setImage] = useState(null)
  const { t } = useTranslation()
  const latestUpload = useRef(0)
  const context = useRef(storageCompanyId)
  context.current = storageCompanyId

  const { execute: getPresignedUploadUrl } = useFetch(
    storageAssetType
      ? APIS.FILES.GET_COMPANY_STORAGE_UPLOAD_URL
      : APIS.FILES.GET_PRESIGNED_UPLOAD_URL
  )
  const { execute: getPresignedDownloadUrl } = useFetch(
    storageAssetType
      ? APIS.FILES.GET_COMPANY_STORAGE_DOWNLOAD_URL
      : APIS.FILES.GET_PRESIGNED_DOWNLOAD_URL
  )

  const uploadButton = useMemo(() => {
    return (
      <Box>
        {isLoading ? <LoadingOutlined /> : <PlusOutlined />}
        <Box>Upload</Box>
      </Box>
    )
  }, [isLoading])

  const imageWidget = useMemo(() => {
    return (
      <ImageWidgetStyled
        src={image}
        alt={name}
        onError={() => setImage(null)}
      />
    )
  }, [image, name])

  const errorMessageWidget = useMemo(() => {
    return (
      error &&
      props.touched && (
        <ErrorMessage className={`error-text ${error ? 'input-error' : ''}`}>
          {error}
        </ErrorMessage>
      )
    )
  }, [error, props.touched])

  const helperMessageWidget = useMemo(() => {
    return (
      helperMessage && (
        <HelperMessage className="helper-text">{helperMessage}</HelperMessage>
      )
    )
  }, [helperMessage])

  useEffect(() => {
    let active = true
    setImage(null)
    if (storageAssetType) {
      if (value) {
        try {
          getPresignedDownloadUrl(
            buildCompanyStorageRequest(
              storageCompanyId,
              storageAssetType,
              value
            ),
            {
              onSuccess: ({ data }) => {
                if (active) setImage(data.url)
              },
              onFailed: () => {
                if (active) {
                  callToast({
                    type: 'error',
                    message: t('common.toast.get.failed')
                  })
                }
              }
            }
          )
        } catch {
          callToast({ type: 'error', message: t('common.toast.get.failed') })
        }
      }
      return () => {
        active = false
      }
    }
    if (regex.input.image.test(value)) {
      const splitValue = value.split('.')

      const payload = {
        [uploadKey]: splitValue[0],
        fileExtension: '.' + splitValue[1],
        ...optionalPayload
      }

      getPresignedDownloadUrl(
        { payload },
        {
          onSuccess: ({ data: { url } }) => {
            if (active) {
              setImage(url)
              setIsLoading(false)
            }
          }
        }
      )
    }
    return () => {
      active = false
    }
  }, [
    getPresignedDownloadUrl,
    optionalPayload,
    storageAssetType,
    storageCompanyId,
    t,
    uploadKey,
    value
  ])

  useEffect(() => {
    latestUpload.current += 1
    setIsLoading(false)
    return () => {
      latestUpload.current += 1
    }
  }, [storageCompanyId])

  const uploadHandler = useCallback(
    ({ file }) => {
      if (file && file.size <= UPLOAD_POLICIES.IMAGE.SIZE) {
        const uploadId = ++latestUpload.current
        const isCurrent = () =>
          uploadId === latestUpload.current &&
          context.current === storageCompanyId
        setIsLoading(true)
        setImage(null)

        const payload = {
          [uploadKey]: uuid(),
          fileExtension: '.' + getFileExtension(file),
          ...optionalPayload
        }
        let request = { payload }
        try {
          if (storageAssetType) {
            request = buildCompanyStorageRequest(
              storageCompanyId,
              storageAssetType,
              `${payload[uploadKey]}${payload.fileExtension}`,
              'upload'
            )
          }
        } catch {
          setIsLoading(false)
          callToast({ type: 'error', message: t('error.default.file-upload') })
          return
        }

        const callback = {
          onSuccess: async ({ data }) => {
            if (!isCurrent()) return
            try {
              const key = storageAssetType
                ? data?.objectKey?.split('/').pop()
                : data?.key
              if (!name || !key || !data.url)
                throw new Error('Invalid upload response')
              await axios.put(data.url, file, {
                headers: {
                  'Content-Type': mime.lookup(file.name)
                }
              })

              if (isCurrent()) setFieldValue(name, key)
            } catch {
              if (isCurrent())
                callToast({
                  type: 'error',
                  message: t('error.default.file-upload')
                })
            }
          },
          onFailed: () => {
            if (isCurrent())
              callToast({
                type: 'error',
                message: t('error.default.file-upload')
              })
          },
          onFinally: () => {
            if (isCurrent()) setIsLoading(false)
          }
        }

        getPresignedUploadUrl(request, callback)
      } else {
        callToast({
          type: 'error',
          message: 'Exceed limit'
        })
      }
    },
    [
      getPresignedUploadUrl,
      name,
      optionalPayload,
      setFieldValue,
      storageAssetType,
      storageCompanyId,
      t,
      uploadKey
    ]
  )

  return (
    <Box className={className}>
      {label && (
        <FieldLabel htmlFor={name}>
          <span>{label}</span>
          {required && <span className="star">*</span>}
        </FieldLabel>
      )}
      <StyledUploadPicField
        listType="picture-card"
        customRequest={uploadHandler}
        showUploadList={false}
        maxCount={1}
        accept={UPLOAD_POLICIES.IMAGE.MIME_TYPE}
      >
        {image ? imageWidget : uploadButton}
      </StyledUploadPicField>
      {errorMessageWidget}
      {helperMessageWidget}
    </Box>
  )
}

export default UploadPicField
